import {useLayoutTrackingRef as useTrackingRef} from '@github-ui/use-tracking-ref'
import {GearIcon} from '@primer/octicons-react'
import {Button, useConfirm} from '@primer/react'
import {memo, useCallback, useImperativeHandle, useMemo, useRef} from 'react'

import {SearchBarUI} from '../../api/stats/contracts'
import {shortcutFromEvent, SHORTCUTS} from '../../helpers/keyboard-shortcuts'
import {isViewConfigDirty} from '../../hooks/use-view-state-reducer/get-view-dirty-states'
import {useViews} from '../../hooks/use-views'
import {useViewOptionsMenuRef} from '../../state-providers/view-options/use-view-options-menu-ref'
import {Resources} from '../../strings'
import {MemexTimestampFieldsPopover} from '../user-notices/memex-timestamp-fields-popover'
import {DirtyViewIndicator} from '../view-options/menu-anchor'
import {FilterBar, type FilterBarProps} from './filter-bar'
import {FilterContainer} from './filter-container'
import {FilterCounterLabel} from './filter-counter-label'
import {FilterInputActions} from './filter-input-actions'
import styles from './tokenized-filter-input.module.css'

export type TokenizedFilterInputProps = {
  /**
   * Optional ID to pass to the Filter component
   */
  id?: string
  /**
   * The value of the input. It will be parsed and displayed as colored
   * tokens, as well as populate the input
   */
  value: string
  /**
   * Optional label for the region landmark wrapping filter controls. Omit
   * if no grouping is needed (e.g. there are no controls besides the input itself).
   */
  regionLabel?: string
  /**
   * Accessible label for the form element.
   */
  formLabel?: string
  /**
   * A handler for the input value change
   */
  onChangeValue: (value: string) => void
  /**
   * A handler for the input keydown event
   */
  onInputKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  /**
   * A ref for the input, optional
   */
  inputRef?: React.RefObject<HTMLInputElement | null>
  /**
   * When given, a count of results is displayed to the right of the input.
   */
  filterCount?: number | null | undefined
  /**
   * An optional handler for the reset changes button, when passed the button will be shown
   */
  onResetChanges?: (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => void
  /**
   * An optional handler for the save changes button, when passed the button will be shown
   */
  onSaveChanges?: (e?: React.SyntheticEvent) => void
  /**
   * Optional; customize text to display on the save changes button
   */
  saveButtonText?: string
  /**
   * Whether to hide the save button; defaults to false
   */
  hideSaveButton?: boolean
  /**
   * Whether to hide the reset changes button; defaults to false
   */
  hideResetChangesButton?: boolean
  /**
   * Whether to hide the counter label; defaults to false
   */
  hideCounterLabel?: boolean
  /**
   * Whether to hide the Display button; defaults to false
   */
  hideDisplayButton?: boolean
  /**
   * Whether to hide the split save button; defaults to false
   */
  hideSplitSaveButton?: boolean
} & Pick<FilterBarProps, 'limitedProviderKeys' | 'onValidation' | 'context' | 'groupAndKeywordSupport'>

export const TokenizedFilterInput = memo<React.PropsWithChildren<TokenizedFilterInputProps>>(
  function TokenizedFilterInputComponent({
    id,
    value,
    regionLabel,
    onChangeValue,
    onInputKeyDown,
    formLabel,
    inputRef: givenInputRef,
    filterCount,
    onResetChanges,
    onSaveChanges,
    saveButtonText,
    hideSaveButton = false,
    hideCounterLabel = false,
    hideResetChangesButton = false,
    hideDisplayButton = false,
    hideSplitSaveButton = false,
    children,
    onValidation,
    limitedProviderKeys,
    context,
    groupAndKeywordSupport,
  }) {
    const inputRef = useRef<HTMLInputElement>(null)
    const confirm = useConfirm()
    useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(givenInputRef, () => inputRef.current)

    const onInputKeyDownRef = useTrackingRef(onInputKeyDown)
    const handleInputKeyDown: React.KeyboardEventHandler<HTMLInputElement> = useCallback(
      e => {
        switch (shortcutFromEvent(e)) {
          case SHORTCUTS.ESCAPE: {
            onInputKeyDownRef.current?.(e)
            return
          }
          case SHORTCUTS.ARROW_DOWN: {
            // TODO: Should only trigger callback when Filter suggestions are hidden
            // https://github.com/github/projects-platform/issues/3408
            // onInputKeyDownRef.current?.(e)
            return
          }
          case SHORTCUTS.TAB: {
            // When a value is present, defer to default Filter component behavior
            if (value.length > 0) return
            onInputKeyDownRef.current?.(e)
            return
          }
        }
      },
      [onInputKeyDownRef, value.length],
    )

    const handleResetChanges: React.MouseEventHandler<HTMLButtonElement> = useCallback(
      event => {
        event.stopPropagation()
        onResetChanges?.(event)
        inputRef.current?.focus()
      },
      [onResetChanges],
    )

    const displayButtonVisible = !hideDisplayButton
    const {currentView, isViewStateDirty, saveCurrentViewState} = useViews()

    const configDirty = currentView ? isViewConfigDirty(currentView) : false
    const filterDirty = currentView?.isFilterDirty ?? false
    const isConfigDirty = displayButtonVisible ? configDirty : isViewStateDirty

    const handleSaveChanges = useCallback(
      async (event?: React.SyntheticEvent) => {
        event?.stopPropagation()
        if (!onSaveChanges) {
          return
        }

        if (displayButtonVisible) {
          const shouldSave = await confirm({
            title: Resources.getConfirmationTitle({filterDirty, configDirty, viewName: currentView?.name}),
            content: Resources.getConfirmationDescription({filterDirty, configDirty}),
            confirmButtonContent: Resources.saveChanges,
            confirmButtonType: 'primary',
            width: 'large',
          })

          if (shouldSave) {
            // When both filter and config are dirty, save everything at once
            if (filterDirty && configDirty && currentView?.number !== undefined) {
              await saveCurrentViewState(currentView.number, {ui: SearchBarUI})
            } else {
              onSaveChanges()
            }
          }
        } else {
          onSaveChanges()
        }

        inputRef.current?.focus()
      },
      [confirm, configDirty, currentView, displayButtonVisible, filterDirty, onSaveChanges, saveCurrentViewState],
    )

    const hasResetAction = Boolean(onResetChanges) && !hideResetChangesButton
    const hasSaveAction = Boolean(onSaveChanges) && !hideSaveButton
    const shouldRenderFilterInputActions = hasResetAction || hasSaveAction
    const {anchorRef, toggleMenu, isMenuOpen} = useViewOptionsMenuRef()

    const handleDisplayButtonClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
      event => {
        event.stopPropagation()
        toggleMenu()
      },
      [toggleMenu],
    )

    const trailingVisual = useMemo(
      () => <FilterCounterLabel hideCounterLabel={hideCounterLabel} filterCount={filterCount} />,
      [hideCounterLabel, filterCount],
    )

    return (
      <FilterContainer label={regionLabel}>
        <div className={styles.Box}>
          <FilterBar
            id={id}
            filterValue={value}
            onChange={onChangeValue}
            onValidation={onValidation}
            onKeyDown={handleInputKeyDown}
            formLabel={formLabel}
            inputRef={inputRef}
            context={context}
            limitedProviderKeys={limitedProviderKeys}
            trailingVisual={trailingVisual}
            groupAndKeywordSupport={groupAndKeywordSupport}
          />
        </div>
        {shouldRenderFilterInputActions && (
          <>
            <FilterInputActions
              hideSaveButton={hideSaveButton}
              hideSplitSaveButton={hideSplitSaveButton}
              hideResetChangesButton={hideResetChangesButton}
              onResetChanges={onResetChanges ? handleResetChanges : undefined}
              onSaveChanges={onSaveChanges && !hideSaveButton ? handleSaveChanges : undefined}
              saveButtonText={saveButtonText}
            >
              {children}
            </FilterInputActions>
            {displayButtonVisible && <div className={styles.Divider} aria-hidden="true" role="presentation" />}
          </>
        )}
        {displayButtonVisible ? (
          <div className={styles.viewButtonContainer}>
            <Button
              ref={anchorRef}
              leadingVisual={
                <DirtyViewIndicator isViewStateDirty={isConfigDirty}>
                  <GearIcon />
                </DirtyViewIndicator>
              }
              onClick={handleDisplayButtonClick}
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
            >
              View
            </Button>
            <MemexTimestampFieldsPopover />
          </div>
        ) : null}
      </FilterContainer>
    )
  },
)
